import { BUSINESS_TIMEZONE } from '@omecare/utils';
import { fr } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';

// Brand label flagging these hours as coming from the legacy Skello import,
// to distinguish them from the newer planning-grid source.
const LEGACY_SOURCE_LABEL = 'Skello';

type MedicalAssistantWorkPeriod = {
  startTime: Date;
  endTime: Date;
  date: Date;
};

type MedicalAssistantWorkPeriodsDisplayProps = {
  workPeriods: MedicalAssistantWorkPeriod[];
};

// Helper function to format time in French format (HH'h'mm)
const formatTime = (timeDate: Date): string => {
  return formatInTimeZone(timeDate, BUSINESS_TIMEZONE, "HH'h'mm", {
    locale: fr,
  });
};

// Helper function to calculate total duration in minutes
const calculateTotalDurationMinutes = (
  periods: MedicalAssistantWorkPeriod[],
): number => {
  return periods.reduce((total, period) => {
    const durationMs = period.endTime.getTime() - period.startTime.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    return total + durationMinutes;
  }, 0);
};

// Helper function to format duration in human-readable format (e.g., "2h30", "1h", "45m")
const formatDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
};

export const MedicalAssistantWorkPeriodsDisplay = ({
  workPeriods,
}: MedicalAssistantWorkPeriodsDisplayProps) => {
  const { t } = useTranslation();

  if (!workPeriods || workPeriods.length === 0) {
    return null;
  }

  const totalDurationMinutes = calculateTotalDurationMinutes(workPeriods);
  const formattedDuration = formatDuration(totalDurationMinutes);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 font-normal">
        <span>{t('medical-assistant-work-periods.title')}</span>
        <Badge
          variant="outline"
          className="px-1.5 py-0 text-[10px] text-muted-foreground"
        >
          {LEGACY_SOURCE_LABEL}
        </Badge>
      </div>
      <div className="text-muted-foreground text-sm">
        {t('medical-assistant-work-periods.total-duration', {
          duration: formattedDuration,
        })}
      </div>
      <ul className="list-inside list-disc space-y-0.5 text-muted-foreground text-sm">
        {workPeriods.map((period, idx) => (
          <li key={`${period.date.getTime()}-${period.startTime.getTime()}`}>
            {t('medical-assistant-work-periods.period-label', {
              number: idx + 1,
              startTime: formatTime(period.startTime),
              endTime: formatTime(period.endTime),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
};
